import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { configure } from "mobx";
import {
  Box,
  FormControl,
  SimpleGrid,
  Text,
  Input,
  Avatar,
  Tag,
  TagLabel,
  Alert,
  AlertIcon,
  Button,
  Select
} from '@chakra-ui/react';
import { useBookAppointmentStore } from './store';
import {
  useUserQuery,
  usePatientLazyQuery,
  useClinicsQuery,
  useScriptRulesQuery,
  useReferralRulesQuery,
  useUpcomingAppointmentsQuery,
  useCancelConsultMutation,
  useServiceTypesQuery,
  useAppointmentTypesQuery,
} from './graphql/types-and-hooks';
import DoctorCard from './components/DoctorCard';
import AppointmentCard from './components/AppointmentCard';
import LoginModal from './components/LoginModal';
import { Loading } from './components/Loading';
import { initAmplify } from './services/amplify';
import UserDetails from './components/UserDetails';
import queryString from 'query-string';
import CreateFamilyMember from './components/CreateFamilyMember';
import { IoIosArrowForward } from "react-icons/io";
import { logError } from './components/logger';
import Banner from './components/Banner';
import Banner1 from './components/Banner1';
import { PiPrescriptionBold } from "react-icons/pi";
import { VscReferences } from "react-icons/vsc";
import RepeatScriptModal from './components/RepeatScriptModal';
import RequestReferralModal from './components/RequestReferralModal';
import { DateTime } from 'luxon';
import { Auth } from 'aws-amplify';
import AppSkeleton from './components/AppSkeleton';
import CardSkeleton from './components/CardSkeleton';
import OneCardSkeleton from './components/OneCardSkeleton';
import { sessionManager } from './managers/SessionManager';
import { useAlert } from './components/AlertProvider';
// import { doctorsStore } from './store/DoctorsStore';
import { appointmentTypesStore } from './store/AppointmentTypesStore';
import { IoIosSearch } from "react-icons/io";
import BookingFlowModal from './components/BookingFlowModal';
import { bookingFlowStore } from './store/BookingFlowStore';
import { servicebookingFlowStore } from './store/ServiceBookingFlowStore';
import { patientStore } from './store/PatientStore';
import Footer from './components/Footer'
import { getDoctorsWithFirstAvailableByUserFkAndLocation } from './managers/doctors';
import { clinicStore } from './store/ClinicStore';
import logger from './utils/logger';
// AWS Amplify initialization
initAmplify();

// Configure MobX globally before any store is initialized
configure({
  isolateGlobalState: true,
  // You might also want these settings for optimal performance:
  enforceActions: "always",
  computedRequiresReaction: true,
  reactionRequiresObservable: true,
  observableRequiresReaction: true
});

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// Access url query variables
const USER_FK = parsed.cid || '';
const CLINIC_GROUP_CRM = parsed.gid || '';
const LOCATION_ID = parsed.lid || '';

const App = () => {

  // eslint-disable-next-line
  const [patient, appointmentType, clinic, setData, resetData] = useBookAppointmentStore((state) => [
    state.patient,
    state.appointmentType,
    state.clinic,
    state.setData,
    state.resetData,
  ]);

  // eslint-disable-next-line
  const [clinicmsg1, setClinicmsg1] = useState('');
  // eslint-disable-next-line
  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line
  const [isCreateFamilyMemberModalOpen, setIsCreateFamilyMemberModalOpen] = useState(false);
  // eslint-disable-next-line
  const [isCreateFamilyMemberOpen, setIsCreateFamilyMemberOpen] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  // eslint-disable-next-line
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const [firstAvailableDoctors, setFirstAvailableDoctors] = useState([]);


  const openCreateFamilyMember = () => setIsCreateFamilyMemberOpen(true);
  const closeCreateFamilyMember = () => setIsCreateFamilyMemberOpen(false);
  // eslint-disable-next-line
  const { data: userData, refetch: refetchUser } = useUserQuery({
    variables: {},
  });

  const user = userData?.user;
  const isLoggedIn = user;

  const [hasClassBAuth, setHasClassBAuth] = useState(null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);


  const { showAlert } = useAlert()

  useEffect(() => {
    sessionManager.setShowAlert(showAlert);
    sessionManager.init({
      groupCRM: CLINIC_GROUP_CRM,
      userFK: USER_FK,
      locationId: LOCATION_ID
    });
  }, [showAlert])


  const openBookingFlowModal = () => {
    servicebookingFlowStore.setServiceBooking(false);
    bookingFlowStore.setIsModalOpen(true);
  }

  const closeBookingFlowModal = () => {
    bookingFlowStore.setIsModalOpen(false);
  }

  const getAuthLevelFromToken = async () => {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken();
      const payload = accessToken?.payload;
      const authLevel = payload.FDAccessLevel || 'A';
      logger.info('authLevel', authLevel);
      setHasClassBAuth(authLevel);
      return authLevel;
    } catch (error) {
      logger.error('Error getting auth level from token', error);
      logError('Error getting auth level from token', error);
      return null;
    }
  };

  useEffect(() => {
    getAuthLevelFromToken();
  }, []);


  const [getPatient] = usePatientLazyQuery();

  const [isModalOpen1, setIsModalOpen1] = useState(false);
  const [isModalOpen2, setIsModalOpen2] = useState(false);

  const { data: clinicsData } = useClinicsQuery({
    variables: {
      input: {
        clinic_group_crm: CLINIC_GROUP_CRM,
      },
    },
  });

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const appointmentTypeId = selectedServiceType ? appointmentTypesStore.selectedAptType?.appointment_type_id : null;
        const response = await getDoctorsWithFirstAvailableByUserFkAndLocation(
          USER_FK,
          LOCATION_ID,
          selectedServiceType || undefined,
          appointmentTypeId || undefined
        );
        setFirstAvailableDoctors(response || []);
      } catch (err) {
        logError('Failed to fetch first available doctors:', err);
        console.error('Failed to fetch first available doctors:', err);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
    // eslint-disable-next-line
  }, [USER_FK, LOCATION_ID, selectedServiceType, appointmentTypesStore.selectedAptType]);



  const { data: referralRulesData, loading: referralRulesLoading } = useReferralRulesQuery({
    variables: {
      input: {
        clinic_group_crm: CLINIC_GROUP_CRM,
        user_fk: USER_FK,
      },
    },
  });

  const { data: scriptRulesData, loading: scriptRulesLoading } = useScriptRulesQuery({
    variables: {
      input: {
        clinic_group_crm: CLINIC_GROUP_CRM,
        user_fk: USER_FK,
      },
    },
  });


  const { data: serviceTypesData, loading: serviceTypesLoading, error: serviceTypesError } =
    useServiceTypesQuery({
      variables: {
        input: {
          clinic_group_crm: CLINIC_GROUP_CRM,
          user_fk: USER_FK,
          location_id: LOCATION_ID,
        },
      },
      skip: !clinic || !clinic.clinic_group_crm || !clinic.user_fk || !clinic.location_id,
    });
    console.log('serviceTypesData', serviceTypesData);
    console.log('serviceTypesLoading', serviceTypesLoading);
    console.log('serviceTypesError', serviceTypesError);

  const { data: appointmentTypesData, loading: appointmentTypesLoading, error: appointmentTypesError } =
    useAppointmentTypesQuery({
      variables: {
        input: {
          clinic_group_crm: CLINIC_GROUP_CRM,
          user_fk: USER_FK,
          location_id: LOCATION_ID,
          service_type_id: selectedServiceType || '',
        },
      },
      skip: !selectedServiceType,
    });

  const serviceTypes = useMemo(() => serviceTypesData?.serviceTypes ?? [], [serviceTypesData]);
  const appointmentTypes = useMemo(() => appointmentTypesData?.appointmentTypes ?? [], [appointmentTypesData]);

  const handleServiceTypeChange = (e) => {
    const serviceTypeId = e.target.value;

    if (!serviceTypeId) {
      setSelectedServiceType(null);
      appointmentTypesStore.setSelectedAptType(null); // Clear appointment type
      setData({ appointmentType: null });
    } else {
      setSelectedServiceType(serviceTypeId);
    }
  };

  // Update the useEffect that handles appointment types
  useEffect(() => {
    if (appointmentTypes.length > 0) {
      // Select the first appointment type when they load
      const defaultAppointmentType = appointmentTypes[0];
      appointmentTypesStore.setSelectedAptType(defaultAppointmentType);
      setSelectedAppointmentType(defaultAppointmentType.appointment_type_id);
      setData({ appointmentType: defaultAppointmentType });

      // Update doctors for this appointment type
      sessionManager.updateDoctorsForAppointmentType(defaultAppointmentType);
    }
  }, [appointmentTypes, setData]);

  const handleAppointmentTypeChange = (e) => {
    const appointmentTypeId = e.target.value;
    const selectedAppointmentType = appointmentTypes.find((at) => at.appointment_type_id === appointmentTypeId);

    if (selectedAppointmentType) {
      appointmentTypesStore.setSelectedAptType(selectedAppointmentType);
      setData({ appointmentType: selectedAppointmentType });

      sessionManager.updateDoctorsForAppointmentType(selectedAppointmentType);
    }
  };


  useEffect(() => {
    const sendHeightToParent = () => {

      const contentHeight = document.body.scrollHeight + 200;
      window.parent.postMessage({ type: 'resizeIframe', height: contentHeight }, '*');
    };

    setTimeout(() => {
      sendHeightToParent();
    }, 800);

  }, []);

  const fetchData = useCallback(async () => {
    if (!user || !getPatient) return;

    try {
      const patientData = await getPatient({
        variables: {
          input: {
            clinic_group_crm: CLINIC_GROUP_CRM,
            user_fk: USER_FK,
            location_id: LOCATION_ID,
            dob: user.dob,
            first_name: user.first_name,
            last_name: user.last_name,
            contact_number: user.phone_number,
          },
        },
      });

      const patientInfo = {
        id: patientData?.data?.patient?.patient_id,
        type: 'myself',
        first_name: user.first_name,
        last_name: user.last_name,
        dob: user.dob,
        contact_number: user.phone_number,
        is_new_pt: patientData?.data?.patient?.is_new_pt,
        last_seen_did: patientData?.data?.patient?.last_seen_did,
      };

      // Save to both stores
      setData({ patient: patientInfo });
      patientStore.setCurrentPatient(patientInfo);
      patientStore.setMainLoginPatient(patientInfo);

      sessionManager.setPatientId(patientData?.data?.patient?.patient_id);
      sessionManager.setIsNewPatient(patientData.data.patient.is_new_pt === '0' ? false : true);
    } catch (error) {
      const referenceCode = logError(error, { location: 'App: useEffect -> fetchData' });
      console.error(`Error fetching patient data (Reference Code: ${referenceCode}):`, error);
      logError('Error fetching patient data', { referenceCode, error });
    }
  }, [user, getPatient, setData]);

  // Now properly use useEffect to trigger fetchData
  useEffect(() => {
    fetchData();
  }, [fetchData]);


  useEffect(() => {
    if (clinicsData) {
      const currentClinic = clinicsData.clinics?.find((clinic) => clinic.user_fk === USER_FK && clinic.location_id === LOCATION_ID);

      if (!currentClinic) {
        showAlert({
          severity: "error",
          title: "Whoops!",
          message: "There was a problem finding the clinic with the current configuration."
        })
        return;
      }

      setData({
        clinic: currentClinic,
      });

      clinicStore.setCurrentClinic(currentClinic);

    }
    // eslint-disable-next-line
  }, [clinicsData, setData, showAlert]);



  // eslint-disable-next-line 
  const [cancelConsultMutation, { loading: cancelConsultLoading }] =
    useCancelConsultMutation({
      refetchQueries: [
        'User',
        'UpcomingAppointments',
        'AppointmentSlots',
        'Calendar',
      ],
      awaitRefetchQueries: true,
    });



  const {
    data: upcomingAppointmentsData,
    loading: upcomingAppointmentsLoading,
    error: upcomingAppointmentsError,
    refetch: refetchUpcomingAppointments,
  } = useUpcomingAppointmentsQuery();


  const formattedAppointmentsData = useMemo(() => {
    const appointments = upcomingAppointmentsData?.upcomingAppointments?.filter(
      appointment =>
        DateTime.fromISO(`${appointment.date}T${appointment.time}:00`) >
        DateTime.now()
    );

    return appointments || [];
  }, [upcomingAppointmentsData]);

  const handleCancelAppointment = async (appointment) => {
    try {
      // If we cannot determine the cancellation period we prompt the user to contact the clinic
      if (!appointment?.cancellation_period && !appointment?.cancellation_period == null) {
        // Show alert to user
        showAlert({
          severity: "warning",
          title: "Can't Cancel",
          message: "Can not cancel appointment within cancellation period",
        });
        return;
      }



      const cancellationTime = appointment?.cancellation_period != null
        ? DateTime.now().plus({ hours: parseInt(appointment.cancellation_period) })
        : DateTime.now();

      const appointmentTime = DateTime.fromISO(`${appointment.date}T${appointment.time}:00`);

      if (cancellationTime > appointmentTime) {
        // Show alert to user
        showAlert({
          severity: "warning",
          title: "Can't Cancel",
          message: "Can not cancel appointment after appointment time"
        })
        return;
      }

      await cancelConsultMutation({
        variables: {
          input: {
            clinic_group_crm: appointment.clinic_group_crm,
            user_fk: appointment.user_fk,
            location_id: appointment.location_id,
            appointment_id: appointment.appointment_id,
          },
        },
      });

      // GTAG EVENT
      window.gtag('event', 'booking_cancelled', {
        clinic_group_crm: appointment.clinic_group_crm,
        user_fk: appointment.user_fk,
        location_id: appointment.location_id,
      });


      await refetchUpcomingAppointments();

    } catch (error) {
      logError('Error cancelling appointment', error);

      return (null);
    }

    return null;
  };


  const renderAppointments = () => {
    if (upcomingAppointmentsLoading) return <Loading height={100} />;
    if (upcomingAppointmentsError) {
      return null;
    }
    if (!formattedAppointmentsData.length) return null;

    return formattedAppointmentsData.map(appointment => {
      const date = DateTime.fromISO(appointment.date);
      const day = date.day;
      const weekday = date.weekdayLong;
      const month = date.monthLong;
      const time = DateTime.fromISO(`${appointment.date}T${appointment.time}`).toFormat('hh:mm a');

      return (
        <AppointmentCard
          key={appointment.appointment_id}
          date={day}
          day={weekday}
          month={month}
          time={time}
          doctorName={appointment.doctor_full_name}
          clinicName={appointment.location_display_name}
          appointmentType={appointment.appointment_type_name}
          patientName={`${appointment.first_name} ${appointment.last_name}`}
          appointmentId={`${appointment.appointment_id}`}
          onCancel={() => handleCancelAppointment(appointment)}
        />

      );
    });
  };

  useEffect(() => {
    refetchUpcomingAppointments();
  }, [refetchUpcomingAppointments]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    closeBookingFlowModal();
    bookingFlowStore.resetFlow();

  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const filteredDoctors = useMemo(() => {
    if (!searchQuery.trim()) return firstAvailableDoctors;

    const lowerCaseQuery = searchQuery.trim().toLowerCase();

    return firstAvailableDoctors.filter((doctor) => {
      const doctorName = doctor?.doctor_name || doctor?.display_name || '';
      return doctorName.toLowerCase().includes(lowerCaseQuery);
    });
  }, [searchQuery, firstAvailableDoctors]);

  if (!bookingFlowStore.isModalOpen && (scriptRulesLoading || referralRulesLoading || sessionManager.isLoadingAppointments || sessionManager.isLoadingDoctorsAvailable)) {
    return <AppSkeleton />;
  }

  return (
    <main>
      <Banner1 />
      <Box
        display="flex"
        flexDirection={{ base: "column", lg: "row" }}
        width={{ base: "100%", lg: "85%" }}
        overflowX="hidden"
        p={3}
        gap="5"
        margin="auto"
        mt="5"
      >
        <Box w={{ base: '100%', lg: "70%" }} >

          <Tag
            onClick={() => {
              isLoggedIn ? toggleDrawer() : setIsModalOpen(true);
            }}
            mb={8}
            size='lg'
            colorScheme={
              hasClassBAuth === "B"
                ? 'green'
                : isLoggedIn
                  ? 'blue'
                  : 'red'
            }
            borderRadius="full"
            _hover={{ cursor: "pointer" }}
            width="100%"
            overflowX="hidden"
            display={{ base: 'flex', lg: 'none' }}
            justifyContent="space-between"
            alignItems="center"

          >
            <Avatar
              size='xs'
              ml={-1}
              mr={2}
              bg={
                hasClassBAuth === "B"
                  ? 'green'
                  : isLoggedIn
                    ? 'blue'
                    : '#B92F2F'
              }
            />
            <TagLabel
              color={
                hasClassBAuth === "B"
                  ? 'green'
                  : isLoggedIn
                    ? 'blue'
                    : '#B92F2F'
              }
            >
              {isLoggedIn ? `Hi ${user.first_name}` : 'Login'}
            </TagLabel>
            <IoIosArrowForward
              color={
                hasClassBAuth === "B"
                  ? 'green'
                  : isLoggedIn
                    ? 'blue'
                    : '#B92F2F'
              }
            />
          </Tag>

          <Button borderRadius={'full'} bg={'#B92F2F'} color={'#fff'} width={'100%'} onClick={openBookingFlowModal} display={{ base: 'flex', lg: 'none' }} mb={8}>Book Appointment</Button>

          {clinicmsg1 && (
            <SimpleGrid columns={[1]} mb={5} spacing={4} width={["100%"]}>
              <Alert status='info'>
                <AlertIcon />
                <p dangerouslySetInnerHTML={{ __html: clinicmsg1 }} />
              </Alert>
            </SimpleGrid>
          )}

          <FormControl display="flex" justifyContent="center">
            <Box
              width="100%"
              overflowX="hidden"
              boxShadow="md"
              p="6"
              rounded="md"
              borderWidth={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                overflowX="hidden"
                p="1"
                rounded="full"
                borderWidth="1px"
              >

                <Input
                  placeholder="Search your Doctor"
                  variant="unstyled"
                  ml="4"
                  color="#000"
                  _placeholder={{ color: "gray.500" }}
                  width="100%"
                  overflowX="hidden"
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  size="sm"
                  rounded="full"
                  bg="#B92F2F"
                  color="white"
                  p="2"
                  _hover={{ bg: "red.600" }}
                >
                  <IoIosSearch />
                </Button>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexDirection={{ base: 'column', lg: 'row' }}
                width="100%"
                overflowX="hidden"
                mt="4"
                gap={3}
              >
                {/* Service Type Selector */}
                <Box display="flex" alignItems="center" gap="2" w="100%">
                  <Text fontSize="sm" fontWeight="bold">Service type:</Text>
                  {serviceTypesLoading ? (
                    <Loading />
                  ) : serviceTypesError ? (
                    <Text color="red.500">Error loading service types</Text>
                  ) : (
                    <Select
                      size="sm"
                      color="gray.800"
                      borderRadius="30px"
                      value={selectedServiceType || ''}
                      onChange={handleServiceTypeChange}
                    >
                      <option value="">All</option>
                      {serviceTypes.map((serviceType) => (
                        <option key={serviceType.id} value={serviceType.id}>
                          {serviceType.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Box>


                {/* Appointment Type Selector */}
                <Box display="flex" alignItems="center" gap="2" w="100%">
                  <Text fontSize="sm" fontWeight="bold">Appointment type:</Text>
                  {appointmentTypesLoading ? (
                    <Loading />
                  ) : appointmentTypesError ? (
                    <Text color="red.500">Error loading appointment types</Text>
                  ) : (
                    <Select
                      size="sm"
                      color="gray.800"
                      borderRadius="30px"
                      value={appointmentTypesStore.selectedAptType?.appointment_type_id || ''} // Ensure a default value is set
                      onChange={handleAppointmentTypeChange}
                    >
                      <option value="">All</option>
                      {appointmentTypes.map((appointmentType) => (
                        <option key={appointmentType.appointment_type_id} value={appointmentType.appointment_type_id}>
                          {appointmentType.display_name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Box>
              </Box>
            </Box>
          </FormControl>

          {isLoggedIn && (upcomingAppointmentsLoading || formattedAppointmentsData.length > 0) ? (
            <SimpleGrid columns={[1]} spacing={4} mt={5} width={["100%"]}>
              <Text fontWeight="bold" fontSize={'xl'}> Your Upcoming Appointments </Text>

              {upcomingAppointmentsLoading ? (
                <OneCardSkeleton />
              ) : (
                renderAppointments()
              )}
            </SimpleGrid>
          ) : null}

          <SimpleGrid columns={[1]} spacing={4} mt={5} width={["100%"]}>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Text fontWeight="bold" fontSize="xl">
                {searchQuery.trim()
                  ? "Search Results"
                  : !selectedServiceType || !appointmentTypesStore.selectedAptType
                    ? "All Doctors"
                    : `Doctors for ${appointmentTypesStore.selectedAptType.display_name}`}
              </Text>

              <BookingFlowModal isOpen={bookingFlowStore.isModalOpen} onClose={handleCloseModal} />
            </Box>

            {loadingDoctors ||  filteredDoctors.length === 0 ? (
              <CardSkeleton />
            ) : firstAvailableDoctors.length === 0 ? (
              <Text>No doctors available for the selected criteria.</Text>
            ) : (
              filteredDoctors.map((doctor) => (
                <DoctorCard
                  key={doctor.doctor_id}
                  doctor={doctor}
                  ddate={{
                    fadate: doctor?.first_slot?.date || "Unavailable",
                    fatime: doctor?.first_slot?.time || "Unavailable",
                  }}
                  duration={"15"}
                />
              ))
            )}

          </SimpleGrid>



        </Box>
        <Box w={{ base: '100%', lg: "20%" }} minWidth={'300px'}>
          <Box display="flex" flexDirection={"column"} gap={"10px"}>

            <UserDetails isOpen={isDrawerOpen} onClose={toggleDrawer} onOpenCreateFamilyMember={openCreateFamilyMember} isAuthenticated={isLoggedIn} hasClassBAuth={hasClassBAuth} />
            <CreateFamilyMember isOpen={isCreateFamilyMemberOpen} onClose={closeCreateFamilyMember} />
            <Box display={{ base: 'none', lg: 'flex' }} width="100%" overflowX="hidden">
              <Box width="100%" overflowX="hidden">
                <Tag
                  onClick={() => {
                    isLoggedIn ? toggleDrawer() : setIsModalOpen(true);
                  }}
                  size='lg'
                  colorScheme={
                    hasClassBAuth === "B"
                      ? 'green'
                      : isLoggedIn
                        ? 'blue'
                        : 'red'
                  }
                  borderRadius="full"
                  _hover={{ cursor: "pointer" }}
                  width="100%"
                  overflowX="hidden"
                  display={{ base: 'none', lg: 'flex' }}
                  justifyContent="space-between"
                  alignItems="center"

                >
                  <Avatar
                    size='xs'
                    ml={-1}
                    mr={2}
                    bg={
                      hasClassBAuth === "B"
                        ? 'green'
                        : isLoggedIn
                          ? 'blue'
                          : '#B92F2F'
                    }
                  />
                  <TagLabel
                    color={
                      hasClassBAuth === "B"
                        ? 'green'
                        : isLoggedIn
                          ? 'blue'
                          : '#B92F2F'
                    }
                  >
                    {isLoggedIn ? `Hi ${user.first_name}` : 'Login'}
                  </TagLabel>
                  <IoIosArrowForward
                    color={
                      hasClassBAuth === "B"
                        ? 'green'
                        : isLoggedIn
                          ? 'blue'
                          : '#B92F2F'
                    }
                  />
                </Tag>
              </Box>
            </Box>
            <LoginModal isOpen={isModalOpen} onClose={handleCloseModal} />

            <Box display="flex" alignItems="center">
              <Button
                borderRadius={'full'}
                bg={'#B92F2F'}
                color={'#fff'}
                width={'100%'}
                onClick={() => {
                  bookingFlowStore.resetFlow();
                  setData({ doctor: null });
                  openBookingFlowModal();
                }}
                display={{ base: 'none', lg: 'flex' }}
                mb={5}
                mt={5}
              >
                Book Appointment
              </Button>

            </Box>
            <Box display="flex" alignItems="center">



              <Button
                onClick={() => { isLoggedIn ? setIsModalOpen1(true) : setIsModalOpen(true) }}
                leftIcon={<PiPrescriptionBold />}
                variant="outline"
                border="2px"
                size="sm"
                bg="transparent"
                borderColor="#B92F2F"
                color="#B92F2F"
                aria-label='Done'
                fontSize='sm'
                width='100%'
                borderRadius={30}
                isDisabled={scriptRulesData?.scriptRules.enable_scripts === '0'}
              >Repeat Scripts</Button>

            </Box>
            <RepeatScriptModal isOpen={isModalOpen1} onClose={() => setIsModalOpen1(false)} />
            <Box display="flex" alignItems="center">
              <Button
                onClick={() => { isLoggedIn ? setIsModalOpen2(true) : setIsModalOpen(true) }}
                leftIcon={<VscReferences />}
                variant="outline"
                border="2px"
                size="sm"
                bg="transparent"
                borderColor="#B92F2F"
                color="#B92F2F"
                aria-label='Done'
                fontSize='sm'
                width='100%'
                borderRadius={30}
                isDisabled={referralRulesData?.referralRules.enable_referrals === '0'}
              >Request Referral</Button>
            </Box>
            <RequestReferralModal isOpen={isModalOpen2} onClose={() => setIsModalOpen2(false)} />
          </Box>
          <Banner />
        </Box>
      </Box>
      <Footer />
    </main>
  );
};

export default observer(App);
